import {format, parseISO, isValid, startOfWeek, endOfWeek, startOfMonth, endOfMonth} from 'date-fns';

import {MetricData} from '../model/IMetrics';

type AggregationType = 'day' | 'week' | 'month';

type MetricAggregationConfig = Record<
    string,
    Partial<Record<AggregationType, 'sum' | 'avg' | 'last_day'>>
>;


type BucketItem = {
    originalDate: Date;
    item: MetricData;
};

export type AggregatedItem = {
    bucket_start: string;
    bucket_end: string;
    date_metrics: string;
    [key: string]: number | string;
};

type AggregateParams = {
    data: MetricData[];
    aggType: AggregationType;
    periodStart: Date;
    periodEnd: Date;
    selectedMetrics: string[];
    aggregationConfig: MetricAggregationConfig;
};

const normalizeDate = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const getBucketKey = (date: Date, aggType: AggregationType) => {
    if (aggType === 'week') {
        return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    }
    return format(startOfMonth(date), 'yyyy-MM-dd');
};

const getBucketRange = (
    key: string,
    aggType: AggregationType,
    periodStart: Date,
    periodEnd: Date,
) => {
    const rawStart = parseISO(key);

    let rawEnd: Date;

    if (aggType === 'week') {
        rawEnd = endOfWeek(rawStart, { weekStartsOn: 1 });
    } else {
        rawEnd = endOfMonth(rawStart);
    }

    const start = rawStart < periodStart ? periodStart : rawStart;
    const end = rawEnd > periodEnd ? periodEnd : rawEnd;

    return { start, end };
};

const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
};

const aggregateMetric = (
    bucketItems: BucketItem[],
    metricId: string,
    aggMethod: 'sum' | 'avg' | 'last_day',
    bucketStart: Date,
    aggType: AggregationType,
) => {
    switch (aggMethod) {
        case 'sum':
            return bucketItems.reduce(
                (sum, item) => sum + (Number(item.item[metricId as keyof MetricData]) || 0),
                0,
            );

        case 'avg': {
            const daysInPeriod =
                aggType === 'week'
                    ? 7
                    : getDaysInMonth(
                        bucketStart.getFullYear(),
                        bucketStart.getMonth() + 1,
                    );

            const values = new Array(daysInPeriod).fill(0);

            bucketItems.forEach(item => {
                const value = item.item[metricId as keyof MetricData];
                if (value === null || value === undefined) return;

                const dayIndex = Math.floor(
                    (item.originalDate.getTime() - bucketStart.getTime()) /
                    (1000 * 60 * 60 * 24),
                );

                if (dayIndex >= 0 && dayIndex < daysInPeriod) {
                    values[dayIndex] = Number(value);
                }
            });

            return values.reduce((a, b) => a + b, 0) / daysInPeriod;
        }

        case 'last_day': {
            const sorted = [...bucketItems].sort(
                (a, b) =>
                    b.originalDate.getTime() - a.originalDate.getTime(),
            );

            for (const item of sorted) {
                const value = item.item[metricId as keyof MetricData];
                if (value !== null) return Number(value);
            }

            return 0;
        }

        default: {
            const first = bucketItems[0];
            if (!first) return 0;

            return Number(first.item[metricId as keyof MetricData]) || 0;
        }
    }
};

export const aggregateData = ({
    data,
    aggType,
    periodStart,
    periodEnd,
    selectedMetrics,
    aggregationConfig,
}: AggregateParams) => {
    if (aggType === 'day' || data.length === 0) return data;

    const buckets: Record<string, BucketItem[]> = {};

    const normalizedStart = normalizeDate(periodStart);
    const normalizedEnd = normalizeDate(periodEnd);

    // 🔹 Группировка
    data.forEach(item => {
        const date = parseISO(item.date_metrics);
        if (!isValid(date)) return;

        const normalizedDate = normalizeDate(date);

        if (normalizedDate < normalizedStart || normalizedDate > normalizedEnd)
            return;

        const key = getBucketKey(normalizedDate, aggType);

        if (!buckets[key]) buckets[key] = [];

        buckets[key].push({
            originalDate: date,
            item,
        });
    });

    // 🔹 Агрегация
    const aggregated = Object.entries(buckets).map(
        ([key, bucketItems]) => {
            const { start, end } = getBucketRange(
                key,
                aggType,
                normalizedStart,
                normalizedEnd,
            );

            const result: AggregatedItem  = {
                bucket_start: format(start, 'yyyy-MM-dd'),
                bucket_end: format(end, 'yyyy-MM-dd'),
                date_metrics: format(start, 'yyyy-MM-dd'),
            };

            selectedMetrics.forEach(metricId => {
                const config = aggregationConfig[metricId];
                const method = config?.[aggType] || 'last_day';

                result[metricId] = aggregateMetric(
                    bucketItems,
                    metricId,
                    method,
                    start,
                    aggType,
                );
            });

            return result;
        },
    );

    return aggregated.sort(
        (a, b) =>
            new Date(a.date_metrics).getTime() -
            new Date(b.date_metrics).getTime(),
    );
};